import client from './client';
import { EventStatus, WaitlistAnswers, WaitlistStatus, AdminDevToolsLog, QuotaRequest, QuotaUsage, QuotaLimits } from '../types';

export interface AdminUser {
  _id: string;
  name: string;
  email: string;
  picture?: string;
  isOwner: boolean;
  betaApproved: boolean;
  phone?: string;
  phoneVerifiedAt?: string;
  createdAt: string;
  eventCount: number;
  albumCount: number;
  kickedCount: number;
}

export interface AdminEvent {
  _id: string;
  name: string;
  description: string;
  status: EventStatus;
  createdAt: string;
  endedAt?: string;
  photoCount: number;
}

export interface AdminWaitlistUser {
  _id: string;
  name: string;
  email: string;
  picture?: string;
  isOwner: boolean;
  betaApproved: boolean;
}

export interface AdminWaitlistEntry {
  _id: string;
  status: WaitlistStatus;
  answers: WaitlistAnswers;
  submittedAt: string;
  reviewedAt?: string;
  communicationSentAt?: string;
  user: AdminWaitlistUser;
}

export interface AdminAlbumCost {
  albumId: string;
  albumName: string;
  photoCount: number;
  processedCount: number;
  configVersion: number;
  compositorCost: number;
}

export interface AdminEventCost {
  eventId: string;
  eventName: string;
  userId: string;
  userName: string;
  compositor: number;
  storage: number;
  total: number;
  albums: AdminAlbumCost[];
}

export interface AdminUserCost {
  userId: string;
  name: string;
  email: string;
  picture?: string;
  compositor: number;
  storage: number;
  transfer: number;
  total: number;
  eventCount: number;
}

export interface AdminCostBreakdown {
  totals: { compositor: number; storage: number; transfer: number; total: number };
  users: AdminUserCost[];
  events: AdminEventCost[];
  exchangeRate: number;
}

export interface AwsBillingService {
  name: string;
  cost: number;
  percentage: number;
}

export interface AwsBillingTimelineEntry {
  start: string;
  end: string;
  total: number;
  services: Record<string, { cost: number; unit: string }>;
}

export interface AwsBillingData {
  startDate: string;
  endDate: string;
  granularity: 'DAILY' | 'MONTHLY';
  grandTotal: number;
  currency: string;
  exchangeRate: number;
  services: AwsBillingService[];
  timeline: AwsBillingTimelineEntry[];
}

export interface AdminAlbumMemberEntry {
  userId: string;
  name: string;
  email: string;
  picture?: string;
  phone?: string;
  phoneVerifiedAt?: string;
  albumCount: number;
  kickedCount: number;
  latestJoin: string;
}

export interface AdminAlbumMembersResponse {
  members: AdminAlbumMemberEntry[];
  pagination: { page: number; limit: number; total: number; pages: number };
}

export interface AdminUserAlbumEntry {
  albumId: string | null;
  albumName: string;
  eventName: string | null;
  joinedAt: string;
  isKicked: boolean;
}

export interface AdminBlockedIP {
  _id: string;
  ip: string;
  reason: string;
  blockedAt: string;
  blockedUntil: string | null;
  unblockedAt: string | null;
  unblockedBy: string | null;
}

export type OverlayAnchor =
  | 'top-left' | 'top-center' | 'top-right'
  | 'middle-left' | 'middle-center' | 'middle-right'
  | 'bottom-left' | 'bottom-center' | 'bottom-right';
export type OverlaySize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

export interface AdminDefaultBrandingConfig {
  anchor: OverlayAnchor;
  size: OverlaySize;
  opacity: number;
}

export interface AdminDefaultBranding {
  exists: boolean;
  name: string;
  storagePath: string;
  url?: string;
  size?: number | null;
  lastModified?: string | null;
  config: AdminDefaultBrandingConfig;
  propagation?: { matchedAlbums: number; modifiedAlbums: number };
}

export type AdminAffectedAlbumProcessingStatus = 'idle' | 'processing' | 'ready' | 'failed';

export interface AdminAffectedAlbum {
  _id: string;
  name: string;
  eventId: string | null;
  eventName: string | null;
  creatorName: string | null;
  creatorEmail: string | null;
  photoCount: number;
  processingStatus: AdminAffectedAlbumProcessingStatus;
  processedPhotoCount: number;
  configVersion: number;
  isLive: boolean;
  createdAt: string;
  hasDefaultOverlay: boolean;
}

export interface AdminAffectedAlbumsDiagnostics {
  defaultOverlayS3Path: string;
  defaultOverlayRecordCount: number;
  albumsWithDefaultOverlayCount: number;
  albumsWithAnyOverlayCount: number;
  totalAlbumsCount: number;
}

export interface AdminAffectedAlbumsResponse {
  albums: AdminAffectedAlbum[];
  totalPhotos: number;
  diagnostics: AdminAffectedAlbumsDiagnostics;
}

export interface AdminReprocessResponse {
  queued: number;
  totalPhotos: number;
  strategy: 'parallel' | 'queued';
  concurrency: number;
}

export interface AdminUploadError {
  _id: string;
  filename: string;
  errorCode: 'size_exceeded' | 'invalid_file' | 'quota_exceeded' | 'processing_error';
  errorMessage: string;
  failedAt: string;
  eventId: string | null;
  eventName: string | null;
  eventStatus: string | null;
  userName: string | null;
  userEmail: string | null;
  userPicture: string | null;
}

export interface AdminUploadErrorsResponse {
  total: number;
  page: number;
  pages: number;
  items: AdminUploadError[];
}

export const adminApi = {
  listUsers: () =>
    client.get<AdminUser[]>('/admin/users').then((r) => r.data),

  getUserEvents: (userId: string) =>
    client.get<AdminEvent[]>(`/admin/users/${userId}/events`).then((r) => r.data),

  listWaitlist: () =>
    client.get<AdminWaitlistEntry[]>('/admin/waitlist').then((r) => r.data),

  updateWaitlistStatus: (id: string, status: 'approved' | 'rejected') =>
    client
      .patch<{ success: boolean; status: 'approved' | 'rejected'; emailSent: boolean; emailError?: string }>(
        `/admin/waitlist/${id}`,
        { status },
      )
      .then((r) => r.data),

  deleteWaitlistEntry: (id: string) =>
    client.delete(`/admin/waitlist/${id}`).then((r) => r.data),

  listDevToolsLogs: (page = 1) =>
    client
      .get<{ logs: AdminDevToolsLog[]; pagination: { page: number; limit: number; total: number; pages: number } }>(
        '/admin/devtools-logs',
        { params: { page } },
      )
      .then((r) => r.data),

  getCostBreakdown: () =>
    client.get<AdminCostBreakdown>('/admin/costs').then((r) => r.data),

  getAwsBilling: (params: { start: string; end: string; granularity: 'DAILY' | 'MONTHLY' }) =>
    client.get<AwsBillingData>('/admin/aws-billing', { params }).then((r) => r.data),

  listAlbumMembers: (page = 1) =>
    client.get<AdminAlbumMembersResponse>('/admin/album-members', { params: { page } }).then((r) => r.data),

  getUserAlbums: (userId: string) =>
    client.get<{ albums: AdminUserAlbumEntry[] }>(`/admin/album-members/${userId}/albums`).then((r) => r.data),

  // ── Quota management ──────────────────────────────────────────────────────

  listQuotaRequests: (page = 1, status?: string) =>
    client
      .get<{ requests: QuotaRequest[]; pendingCount: number; pagination: { page: number; limit: number; total: number; pages: number } }>(
        '/admin/quota-requests',
        { params: { page, ...(status ? { status } : {}) } },
      )
      .then((r) => r.data),

  updateQuotaRequest: (id: string, data: { status: 'approved' | 'rejected'; newLimit?: number; adminNote?: string }) =>
    client.patch<QuotaRequest>(`/admin/quota-requests/${id}`, data).then((r) => r.data),

  getUserQuota: (userId: string) =>
    client.get<QuotaUsage & { isOverridden: boolean; defaults: QuotaLimits }>(`/admin/users/${userId}/quota`).then((r) => r.data),

  updateUserQuota: (userId: string, data: Partial<QuotaLimits>) =>
    client.patch(`/admin/users/${userId}/quota`, data).then((r) => r.data),

  // ── Security (Blocked IPs) ────────────────────────────────────────────────

  listBlockedIPs: () =>
    client.get<{ blockedIPs: AdminBlockedIP[] }>('/admin/blocked-ips').then((r) => r.data),

  unblockIP: (id: string) =>
    client.delete(`/admin/blocked-ips/${id}`).then((r) => r.data),

  wipeUserAccount: (userId: string, confirmEmail: string) =>
    client
      .delete<{
        success: boolean;
        email: string;
        deleted: Record<string, number>;
      }>(`/admin/users/${userId}/wipe`, { data: { confirmEmail } })
      .then((r) => r.data),

  // ── Services Testing ──────────────────────────────────────────────────────

  testWhatsappTemplate: (payload: { kind: 'approved' | 'rejected'; phone: string; name?: string }) =>
    client
      .post<{ success: boolean; sid: string; status: string }>('/admin/testing/whatsapp', payload)
      .then((r) => r.data),

  testEmailTemplate: (payload: { kind: 'approved' | 'rejected'; email: string; name?: string }) =>
    client
      .post<{ success: boolean; id: string }>('/admin/testing/email', payload)
      .then((r) => r.data),

  // ── Communications ─────────────────────────────────────────────────────────

  sendBulkSignupEmails: (entryIds: string[]) =>
    client
      .post<{
        success: boolean;
        sent: number;
        failed: number;
        results: Array<{ entryId: string; email: string; success: boolean; error?: string }>;
      }>('/admin/communications/send-signup', { entryIds })
      .then((r) => r.data),

  // ── Default Branding ──────────────────────────────────────────────────────

  getDefaultBranding: () =>
    client.get<AdminDefaultBranding>('/admin/branding/default-overlay').then((r) => r.data),

  uploadDefaultBranding: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return client
      .put<AdminDefaultBranding>('/admin/branding/default-overlay', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => r.data);
  },

  updateDefaultBrandingConfig: (config: AdminDefaultBrandingConfig) =>
    client
      .put<AdminDefaultBranding>('/admin/branding/default-overlay/config', config)
      .then((r) => r.data),

  listAffectedAlbums: (includeAll = false) =>
    client
      .get<AdminAffectedAlbumsResponse>('/admin/branding/affected-albums', {
        params: includeAll ? { includeAll: 'true' } : undefined,
      })
      .then((r) => r.data),

  reprocessAffectedAlbums: (albumIds: string[]) =>
    client.post<AdminReprocessResponse>('/admin/branding/reprocess', { albumIds }).then((r) => r.data),

  listUploadErrors: (page = 1) =>
    client.get<AdminUploadErrorsResponse>('/admin/upload-errors', { params: { page } }).then((r) => r.data),
};
